import React, { useEffect, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import dayjs from "dayjs";
import AppTable from "../../common/app-table";
import AppChip from "../../common/app-chip";
import HeightButton from "../../common/height-button";
import typography from "../../cssUtils/typography";
import { useAuth } from "../../hooks/AuthProvider";
import { CAMPAIGN_CANCEL_ENDPOINT, getCampaignBroadcastsEndpoint } from "../../config/urls";
import { ChannelBadge, StatusBadge } from "./campaignBadges";

const formatDate = (value) => (value ? dayjs(value).format("MMM D, YYYY, h:mm A") : "—");
const muted = (value) => <Typography sx={{ ...typography.bodyLarge, color: "#6B7280" }}>{value || "—"}</Typography>;
// Only scheduled WhatsApp broadcasts can be edited / cancelled; RCS gets Details only
const CHANNEL_NAMES = { whatsapp: "WhatsApp", rcs: "RCS" };
// "RCS after 6h · scheduled for 12 Oct, 4:15 PM", "WhatsApp after 2h · skipped (No failed recipients)"
const fallbackText = (fallback) => {
  if (!fallback) return null;
  const base = `${CHANNEL_NAMES[fallback.channel]} after ${fallback.delay_hours}h`;
  if (fallback.status === "pending") return `${base} · waits for this broadcast to finish`;
  if (fallback.status === "scheduled") return `${base} · scheduled for ${formatDate(fallback.scheduled_at)}`;
  return `${base} · ${fallback.status.replace("_", " ")}${fallback.error_message ? ` (${fallback.error_message})` : ""}`;
};

const isEditable = (row) => row.channel === "whatsapp" && row.status === "scheduled";


// One label/value line of the details modal
const DetailRow = ({ label, children }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "140px 1fr" }, gap: { xs: 0.25, sm: 1.5 }, py: 1, borderTop: "1px solid #EEF0F3" }}>
    <Typography sx={{ ...typography.bodySmall, color: "#6B7280" }}>{label}</Typography>
    <Box sx={{ ...typography.bodySmall, color: "#1C2333", fontWeight: 600, minWidth: 0, wordBreak: "break-word" }}>{children || "—"}</Box>
  </Box>
);

const BroadcastDetails = ({ broadcast, campaignName, onClose }) => (
  <Dialog open={!!broadcast} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle sx={{ ...typography.cardTitle, fontWeight: 700 }}>{broadcast?.name || "Broadcast details"}</DialogTitle>
    {broadcast && (
      <DialogContent>
        <DetailRow label="Campaign">{campaignName}</DetailRow>
        <DetailRow label="Channel"><ChannelBadge channel={broadcast.channel} /></DetailRow>
        <DetailRow label="Status"><StatusBadge status={broadcast.status} /></DetailRow>
        <DetailRow label="Broadcast type">{broadcast.audience_type}</DetailRow>
        <DetailRow label="Audience">{broadcast.audience_name}</DetailRow>
        <DetailRow label="Template">{broadcast.template_name}</DetailRow>
        <DetailRow label="Created by">{broadcast.created_by}</DetailRow>
        {/* "Send now" broadcasts also carry scheduled_datetime (= send time), so only scheduled ones show Created + Scheduled for */}
        {broadcast.scheduled_type && broadcast.scheduled_type !== "now" && (
          <>
            <DetailRow label="Created date time">{broadcast.created_at && formatDate(broadcast.created_at)}</DetailRow>
            <DetailRow label="Scheduled for">{broadcast.scheduled_datetime && formatDate(broadcast.scheduled_datetime)}</DetailRow>
          </>
        )}
        <DetailRow label="Broadcast date time">{broadcast.sent_at && formatDate(broadcast.sent_at)}</DetailRow>
        <DetailRow label="Planned">{broadcast.status === "scheduled" && !broadcast.planned ? null : String(broadcast.planned)}</DetailRow>
        <DetailRow label="Actual">{broadcast.status === "scheduled" ? null : String(broadcast.actual)}</DetailRow>
        <DetailRow label="Smart retry">
          {broadcast.smart_retry?.enabled ? `On · after ${broadcast.smart_retry.hours}h` : "Off"}
        </DetailRow>
        <DetailRow label="Fallback">{fallbackText(broadcast.fallback) || "Off"}</DetailRow>
        {broadcast.description && <DetailRow label="Description">{broadcast.description}</DetailRow>}
      </DialogContent>
    )}
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <HeightButton variant="outlined" title="Close" onClick={onClose} />
    </DialogActions>
  </Dialog>
);

// Broadcasts of one campaign (WhatsApp + RCS), loaded when its row is expanded
const CampaignBroadcasts = ({ campaignId, onAddBroadcast, onNavigate }) => {
  const { getValidToken } = useAuth();
  const [broadcasts, setBroadcasts] = useState([]);
  const [campaignName, setCampaignName] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailsRow, setDetailsRow] = useState(null);
  const [cancelRow, setCancelRow] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [menu, setMenu] = useState(null); // { anchor, row }

  const headers = async () => ({
    Authorization: `Bearer ${await getValidToken()}`,
    "Content-Type": "application/json",
    "X-Tenant-Id": localStorage.getItem("tenant"),
  });

  const fetchBroadcasts = async () => {
    setLoading(true);
    try {
      const response = await fetch(getCampaignBroadcastsEndpoint(campaignId), { headers: await headers() });
      if (!response.ok) throw new Error(`Failed to fetch broadcasts: ${response.status}`);
      const data = await response.json();
      setBroadcasts(data.results || []);
      setCampaignName(data.campaign_name || "");
    } catch (error) {
      console.error(error);
      setBroadcasts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBroadcasts();
  }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Opens the Campaigns (Beta) setup page in edit mode; saving goes through the existing scheduled-broadcast edit API
  const editBroadcast = (row) =>
    onNavigate("campaigns_v2_setup", {
      mode: "edit",
      campaignId,
      edit: {
        isEdit: true,
        lastPage: "campaigns_v2",
        campaignId,
        campaignName,
        broadcastName: row.name,
        templateId: row.template_id,
        templateName: row.template_name,
        templateType: row.template_category,
        segmentName: row.audience_type === "Segment" ? row.audience_name : undefined,
        option: row.audience_type,
        audience: {},
        broadcast: { id: row.id, scheduled: row.scheduled_datetime, scheduled_datetime: row.scheduled_datetime },
      },
    });

  const cancelBroadcast = async () => {
    setCancelling(true);
    setCancelError(null);
    try {
      const response = await fetch(`${CAMPAIGN_CANCEL_ENDPOINT}${cancelRow.id}/`, { method: "POST", headers: await headers() });
      if (!response.ok) throw new Error(response.status === 400 ? "This broadcast is no longer scheduled." : `Failed to cancel (${response.status})`);
      setCancelRow(null);
      fetchBroadcasts();
    } catch (error) {
      setCancelError(error.message);
    } finally {
      setCancelling(false);
    }
  };

  const columns = [
    { id: "channel", label: "Channel", render: (row) => <ChannelBadge channel={row.channel} /> },
    {
      id: "name",
      label: "Broadcast name",
      render: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 500 }}>{row.name || "—"}</Typography>
          {row.smart_retry?.enabled && (
            <AppChip label={`Smart retry · ${row.smart_retry.hours}h`} sx={{ bgcolor: "#EEF1FA", color: "#14204D" }} />
          )}
          {row.fallback && (
            <AppChip label={`Fallback → ${CHANNEL_NAMES[row.fallback.channel]} · ${row.fallback.delay_hours}h`} sx={{ bgcolor: "#EEF1FA", color: "#14204D" }} />
          )}
        </Box>
      ),
    },
    { id: "status", label: "Status", render: (row) => <StatusBadge status={row.status} /> },
    { id: "template_name", label: "Template", render: (row) => muted(row.template_name) },
    { id: "sent_at", label: "Broadcast date", render: (row) => muted(row.sent_at && formatDate(row.sent_at)) },
    { id: "next_run", label: "Next run", render: (row) => muted(row.next_run && formatDate(row.next_run)) },
    { id: "planned", label: "Planned", render: (row) => (row.status === "scheduled" && !row.planned ? "—" : row.planned) },
    { id: "actual", label: "Actual", render: (row) => (row.status === "scheduled" ? "—" : row.actual) },
    {
      id: "actions",
      label: "Actions",
      render: (row) => (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setMenu({ anchor: e.currentTarget, row }); }}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ border: "1px solid #E3E5EA", borderRadius: "10px", overflow: "hidden", ml: { xs: 0, sm: 5 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 0.5, bgcolor: "#F6F7F9" }}>
        <Typography sx={{ ...typography.bodySmall, fontWeight: 600, color: "#6B7280" }}>Broadcasts in this campaign</Typography>
        {onAddBroadcast && (
          <Button size="small" onClick={onAddBroadcast} sx={{ textTransform: "none", fontWeight: 600, color: "#14204D" }}>
            + Add broadcast
          </Button>
        )}
      </Box>
      <AppTable
        columns={columns}
        rows={broadcasts}
        rowKey={(row) => `${row.channel}-${row.id}`}
        loading={loading}
        emptyMessage="No broadcasts"
        maxHeight={400}
        autoAdjustHeight={false}
      />

      <Menu
        anchorEl={menu?.anchor}
        open={!!menu}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={() => { setDetailsRow(menu.row); setMenu(null); }}>View Details</MenuItem>
        {menu && isEditable(menu.row) && onNavigate && (
          <MenuItem onClick={() => { editBroadcast(menu.row); setMenu(null); }}>Edit</MenuItem>
        )}
        {menu && isEditable(menu.row) && (
          <MenuItem sx={{ color: "red" }} onClick={() => { setCancelError(null); setCancelRow(menu.row); setMenu(null); }}>Cancel</MenuItem>
        )}
      </Menu>

      <BroadcastDetails broadcast={detailsRow} campaignName={campaignName} onClose={() => setDetailsRow(null)} />

      <Dialog open={!!cancelRow} onClose={() => !cancelling && setCancelRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ ...typography.cardTitle, fontWeight: 700 }}>Cancel broadcast</DialogTitle>
        <DialogContent>
          <Typography sx={{ ...typography.bodyLarge }}>
            Are you sure you want to cancel <b>{cancelRow?.name}</b> (scheduled for {formatDate(cancelRow?.next_run)})? This action cannot be reversed.
          </Typography>
          {cancelError && <Typography sx={{ ...typography.bodySmall, color: "#C0554B", mt: 1.5 }}>{cancelError}</Typography>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <HeightButton variant="outlined" title="Keep it" onClick={() => setCancelRow(null)} disabled={cancelling} />
          <HeightButton variant="contained" title={cancelling ? "Cancelling..." : "Cancel broadcast"} onClick={cancelBroadcast} disabled={cancelling} />
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignBroadcasts;
